<template>
    <div class="message-container">
      <!-- 左侧患者列表 -->
      <div class="patient-list">
        <div class="search-container">
          <el-input
            placeholder="搜索患者姓名/病历号"
            v-model="searchQuery"
            prefix-icon="el-icon-search"
            clearable
            @input="filterPatients"
          >
          </el-input>
        </div>
  
        <el-tabs v-model="patientListTab" type="card" @tab-click="handleTabClick">
          <el-tab-pane label="全部患者" name="all"></el-tab-pane>
          <el-tab-pane label="待处理" name="pending">
            <span slot="label"
              >待处理
              <el-badge
                :value="getPendingCount()"
                class="pending-badge"
                :hidden="getPendingCount() === 0"
              ></el-badge
            ></span>
          </el-tab-pane>
          <el-tab-pane label="异常患者" name="abnormal">
            <span slot="label"
              >异常患者
              <el-badge
                :value="getAbnormalCount()"
                class="abnormal-badge"
                :hidden="getAbnormalCount() === 0"
                type="danger"
              ></el-badge
            ></span>
          </el-tab-pane>
        </el-tabs>
  
        <div class="patient-list-content">
          <div
            v-for="patient in filteredPatients"
            :key="patient.id"
            :class="[
              'patient-item',
              selectedPatient && patient.id === selectedPatient.id
                ? 'active'
                : '',
              patient.status === '异常' ? 'abnormal-patient' : '',
            ]"
            @click="selectPatient(patient)"
          >
            <div class="avatar-container">
              <el-avatar :src="patient.avatar" :size="40"></el-avatar>
              <el-badge
                v-if="patient.unreadCount"
                :value="patient.unreadCount"
                class="item-unread"
              ></el-badge>
              <el-badge
                v-if="patient.status === '异常'"
                value="!"
                class="item-abnormal"
                type="danger"
              ></el-badge>
            </div>
            <div class="patient-info">
              <div class="patient-name">
                {{ patient.name }}
                <span class="patient-age">{{ patient.age }}岁</span>
              </div>
              <div class="latest-message">{{ getLatestMessage(patient) }}</div>
            </div>
            <div class="message-time">
              <div>{{ getLatestMessageTime(patient) }}</div>
              <el-tag size="mini" :type="getStatusType(patient.planStatus)">{{
                patient.planStatus
              }}</el-tag>
            </div>
          </div>
        </div>
      </div>
  
      <!-- 中间消息区域 -->
      <div class="message-area">
        <div v-if="selectedPatient" class="message-header">
          <div class="patient-title">
            <span class="name">{{ selectedPatient.name }}</span>
            <span class="record-number"
              >病历号: {{ selectedPatient.recordNum }}</span
            >
            <el-tag
              size="small"
              :type="getStatusType(selectedPatient.planStatus)"
            >
              {{ selectedPatient.planStatus }}
            </el-tag>
          </div>
          <div class="header-actions">
            <el-dropdown @command="handlePatientAction" trigger="click">
              <el-button type="text">
                更多操作<i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="phone">电话联系</el-dropdown-item>
                <el-dropdown-item command="record">查看随访记录</el-dropdown-item>
                <el-dropdown-item command="plan">查看随访计划</el-dropdown-item>
                <el-dropdown-item command="report">异常报告</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </div>
  
        <div v-if="selectedPatient" class="message-content">
          <div
            v-for="(message, index) in selectedPatient.messages"
            :key="index"
            class="message-block"
          >
            <div
              class="message-date-divider"
              v-if="showDateDivider(message, index)"
            >
              <span>{{ formatFullDate(message.timestamp) }}</span>
            </div>
  
            <div :class="['message-wrapper', getMessageClass(message)]">
              <div class="avatar">
                <el-avatar
                  :size="36"
                  :src="getMessageAvatar(message)"
                ></el-avatar>
              </div>
              <div class="message-bubble">
                <div class="message-info">
                  <span class="sender-name">{{ message.sender }}</span>
                  <span class="sender-role">{{ message.role }}</span>
                  <span class="message-time">{{
                    formatTime(message.timestamp)
                  }}</span>
  
                  <span
                    v-if="message.status"
                    :class="['message-status', `status-${message.status}`]"
                  >
                    {{ getStatusText(message.status) }}
                  </span>
                </div>
  
                <div class="message-text" v-html="message.content"></div>
  
                <!-- 显示图片 -->
                <div
                  v-if="message.images && message.images.length > 0"
                  class="message-images"
                >
                  <el-image
                    v-for="(img, imgIndex) in message.images"
                    :key="imgIndex"
                    :src="img"
                    :preview-src-list="message.images"
                    fit="cover"
                    class="message-image"
                  ></el-image>
                </div>
  
                <!-- 显示附件 -->
                <div
                  v-if="message.attachments && message.attachments.length > 0"
                  class="message-attachments"
                >
                  <div
                    v-for="(attachment, attachmentIndex) in message.attachments"
                    :key="attachmentIndex"
                    class="message-attachment"
                  >
                    <i class="el-icon-document"></i>
                    <span>{{ attachment.name }}</span>
                    <el-button
                      type="text"
                      icon="el-icon-download"
                      @click="downloadAttachment(attachment)"
                      >下载</el-button
                    >
                  </div>
                </div>
  
                <!-- 处理信息 -->
                <div v-if="message.processInfo" class="process-info">
                  <div class="process-detail">
                    <i class="el-icon-info"></i>
                    <span>{{ message.processInfo }}</span>
                  </div>
                </div>
  
                <!-- 转发信息 -->
                <div v-if="message.forwardInfo" class="forward-info">
                  <el-alert
                    :title="message.forwardInfo.title"
                    :type="message.forwardInfo.type"
                    :description="message.forwardInfo.desc"
                    show-icon
                    :closable="false"
                    size="small"
                  ></el-alert>
                </div>
              </div>
            </div>
          </div>
        </div>
  
        <div v-if="selectedPatient" class="message-reply">
          <div class="reply-toolbar">
            <el-button
              type="text"
              icon="el-icon-picture"
              @click="handleUploadImage"
              >添加图片</el-button
            >
            <el-button
              type="text"
              icon="el-icon-paperclip"
              @click="handleUploadFile"
              >添加附件</el-button
            >
  
            <!-- 快捷回复模板 -->
            <el-dropdown @command="handleTemplateCommand" trigger="click">
              <el-button type="text">
                快捷模板<i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item
                  v-for="(template, index) in replyTemplates"
                  :key="index"
                  :command="index"
                >
                  {{ template.title }}
                </el-dropdown-item>
                <el-dropdown-item divided command="manage"
                  >管理模板</el-dropdown-item
                >
              </el-dropdown-menu>
            </el-dropdown>
  
            <!-- 转发给 -->
            <el-dropdown @command="handleForwardCommand" trigger="click">
              <el-button type="text">
                转发给<i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item
                  v-for="option in forwardOptions"
                  :key="option.value"
                  :command="option.value"
                >
                  {{ option.label }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
  
          <el-input
            type="textarea"
            :rows="3"
            placeholder="请输入回复内容..."
            v-model="replyContent"
            resize="none"
          ></el-input>
  
          <div class="upload-preview" v-if="uploadFiles.length > 0">
            <div
              v-for="(file, index) in uploadFiles"
              :key="index"
              class="upload-file"
            >
              <span>{{ file.name }}</span>
              <el-button
                type="text"
                icon="el-icon-delete"
                @click="removeUploadFile(index)"
              ></el-button>
            </div>
          </div>
  
          <!-- 转发信息显示 -->
          <div v-if="forwardTarget" class="forward-target-info">
            <el-alert
              :title="`将转发给: ${getForwardTargetName(forwardTarget)}`"
              type="info"
              :closable="true"
              @close="forwardTarget = null"
              size="small"
            >
            </el-alert>
          </div>
  
          <div class="reply-actions">
            <el-button
              type="primary"
              @click="sendReply"
              :disabled="!canSendReply"
            >
              {{ forwardTarget ? "转发留言" : "发送回复" }}
            </el-button>
          </div>
        </div>
  
        <div v-if="!selectedPatient" class="no-patient-selected">
          <i class="el-icon-message"></i>
          <p>请选择一个患者开始处理留言</p>
        </div>
      </div>
  
      <!-- 右侧患者信息 -->
      <div class="patient-detail" v-if="selectedPatient">
        <div class="patient-profile">
          <div class="profile-header">
            <el-avatar :src="selectedPatient.avatar" :size="80"></el-avatar>
            <h3>{{ selectedPatient.name }}</h3>
            <div class="basic-info">
              <p>
                {{ selectedPatient.gender === "male" ? "男" : "女" }}
                {{ selectedPatient.age }}岁
              </p>
              <p>{{ selectedPatient.diagnosis }}</p>
            </div>
          </div>
  
          <el-divider content-position="left">随访状态</el-divider>
  
          <el-descriptions :column="1" border>
            <el-descriptions-item label="随访阶段">
              <el-tag :type="getPhaseType(selectedPatient.phase)">{{
                selectedPatient.phase
              }}</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="计划状态">
              <el-tag :type="getStatusType(selectedPatient.planStatus)">{{
                selectedPatient.planStatus
              }}</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="满意度">
              <el-rate
                v-model="selectedPatient.satisfaction"
                disabled
                text-color="#ff9900"
              ></el-rate>
            </el-descriptions-item>
            <el-descriptions-item label="依从性">
              <el-progress
                :percentage="selectedPatient.compliance"
                :color="getComplianceColor(selectedPatient.compliance)"
              ></el-progress>
            </el-descriptions-item>
            <el-descriptions-item label="下次随访">
              {{ selectedPatient.nextFollowUp }}
            </el-descriptions-item>
          </el-descriptions>
  
          <!-- 异常提醒 -->
          <div
            v-if="
              selectedPatient.abnormalAlerts &&
              selectedPatient.abnormalAlerts.length > 0
            "
            class="abnormal-alerts"
          >
            <el-divider content-position="left">
              <span class="divider-title warning">异常提醒</span>
            </el-divider>
  
            <div
              v-for="(alert, index) in selectedPatient.abnormalAlerts"
              :key="index"
              class="alert-item"
            >
              <el-alert
                :title="alert.title"
                :type="alert.level"
                :description="alert.description"
                show-icon
                :closable="false"
              ></el-alert>
            </div>
          </div>
  
          <el-divider content-position="left">快捷操作</el-divider>
  
          <div class="quick-actions">
            <el-button type="primary" plain icon="el-icon-view" @click="viewTasks"
              >查看随访任务</el-button
            >
            <el-button
              type="success"
              plain
              icon="el-icon-edit-outline"
              @click="editPlan"
              >编辑随访计划</el-button
            >
            <el-button
              type="warning"
              plain
              icon="el-icon-warning"
              @click="handleAbnormal"
              >处理异常</el-button
            >
            <el-button
              type="info"
              plain
              icon="el-icon-data-line"
              @click="viewSatisfaction"
              >满意度报告</el-button
            >
          </div>
  
          <el-divider content-position="left">最近任务</el-divider>
  
          <div class="recent-tasks">
            <div
              v-for="(task, index) in selectedPatient.recentTasks"
              :key="index"
              class="task-item"
            >
              <div class="task-header">
                <div class="task-title">
                  <i
                    :class="[
                      'task-icon',
                      task.completed ? 'el-icon-check' : 'el-icon-time',
                    ]"
                  ></i>
                  {{ task.title }}
                </div>
                <div class="task-status">
                  <el-tag size="mini" :type="task.completed ? 'success' : 'info'">
                    {{ task.completed ? "已完成" : "待完成" }}
                  </el-tag>
                </div>
              </div>
              <div class="task-time">{{ task.time }}</div>
              <div class="task-desc">{{ task.description }}</div>
              <div class="task-footer">
                <el-button
                  v-if="!task.completed"
                  type="text"
                  size="mini"
                  @click.stop="markTaskDone(task)"
                >
                  标记完成
                </el-button>
                <el-button
                  type="text"
                  size="mini"
                  @click.stop="viewTaskDetail(task)"
                >
                  查看详情
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </div>
  
      <!-- 模板管理对话框 -->
      <el-dialog
        title="快捷回复模板管理"
        :visible.sync="templateDialogVisible"
        width="600px"
      >
        <el-table :data="replyTemplates" style="width: 100%">
          <el-table-column prop="title" label="模板名称"></el-table-column>
          <el-table-column label="操作" width="150">
            <template slot-scope="scope">
              <el-button size="mini" type="text" @click="editTemplate(scope.row)"
                >编辑</el-button
              >
              <el-button
                size="mini"
                type="text"
                @click="deleteTemplate(scope.$index)"
                >删除</el-button
              >
            </template>
          </el-table-column>
        </el-table>
        <div style="margin-top: 20px">
          <el-button type="primary" @click="addNewTemplate">新增模板</el-button>
        </div>
      </el-dialog>
  
      <!-- 编辑模板对话框 -->
      <el-dialog
        :title="currentTemplateAction === 'add' ? '新增模板' : '编辑模板'"
        :visible.sync="editTemplateDialogVisible"
        width="500px"
      >
        <el-form :model="currentTemplate" label-width="80px">
          <el-form-item label="模板名称">
            <el-input v-model="currentTemplate.title"></el-input>
          </el-form-item>
          <el-form-item label="模板内容">
            <el-input
              type="textarea"
              :rows="5"
              v-model="currentTemplate.content"
            ></el-input>
          </el-form-item>
        </el-form>
        <div slot="footer" class="dialog-footer">
          <el-button @click="editTemplateDialogVisible = false">取 消</el-button>
          <el-button type="primary" @click="saveTemplate">确 定</el-button>
        </div>
      </el-dialog>
  
      <!-- 处理异常对话框 -->
      <el-dialog
        title="处理异常情况"
        :visible.sync="abnormalDialogVisible"
        width="500px"
      >
        <div v-if="selectedPatient && selectedPatient.abnormalAlerts">
          <div
            v-for="(alert, index) in selectedPatient.abnormalAlerts"
            :key="index"
            class="abnormal-item"
          >
            <el-alert
              :title="alert.title"
              :type="alert.level"
              :description="alert.description"
              show-icon
              :closable="false"
              style="margin-bottom: 15px"
            ></el-alert>
  
            <el-form label-position="top" class="abnormal-form">
              <el-form-item :label="`处理方案 (${index + 1})`">
                <el-input
                  type="textarea"
                  :rows="2"
                  :placeholder="`请输入对'${alert.title}'问题的处理方案`"
                  v-model="alert.solution"
                ></el-input>
              </el-form-item>
            </el-form>
  
            <div class="abnormal-actions">
              <el-button
                type="primary"
                size="mini"
                @click="resolveAbnormal(alert, index)"
              >
                标记已解决
              </el-button>
              <el-button
                type="warning"
                size="mini"
                @click="escalateAbnormal(alert, index)"
              >
                上报给医生
              </el-button>
            </div>
            <el-divider
              v-if="index < selectedPatient.abnormalAlerts.length - 1"
            ></el-divider>
          </div>
        </div>
        <div slot="footer" class="dialog-footer">
          <el-button @click="abnormalDialogVisible = false">关 闭</el-button>
        </div>
      </el-dialog>
  
      <!-- 随访任务详情对话框 -->
      <el-dialog
        title="随访任务详情"
        :visible.sync="taskDetailDialogVisible"
        width="650px"
      >
        <div v-if="selectedTask">
          <el-descriptions :column="1" border>
            <el-descriptions-item label="任务名称">{{
              selectedTask.title
            }}</el-descriptions-item>
            <el-descriptions-item label="计划执行时间">{{
              selectedTask.time
            }}</el-descriptions-item>
            <el-descriptions-item label="任务状态">
              <el-tag :type="selectedTask.completed ? 'success' : 'info'">
                {{ selectedTask.completed ? "已完成" : "待完成" }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="任务描述">{{
              selectedTask.description
            }}</el-descriptions-item>
            <el-descriptions-item v-if="selectedTask.notes" label="执行记录">{{
              selectedTask.notes
            }}</el-descriptions-item>
          </el-descriptions>
  
          <div
            v-if="selectedTask.subtasks && selectedTask.subtasks.length > 0"
            style="margin-top: 20px"
          >
            <h4>子任务列表</h4>
            <el-table :data="selectedTask.subtasks" style="width: 100%">
              <el-table-column prop="title" label="子任务名称"></el-table-column>
              <el-table-column prop="status" label="状态" width="100">
                <template slot-scope="scope">
                  <el-tag
                    :type="scope.row.completed ? 'success' : 'info'"
                    size="mini"
                  >
                    {{ scope.row.completed ? "已完成" : "待完成" }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="100">
                <template slot-scope="scope">
                  <el-button
                    v-if="!scope.row.completed"
                    type="text"
                    size="mini"
                    @click="markSubtaskDone(scope.row, scope.$index)"
                  >
                    完成
                  </el-button>
                  <span v-else>-</span>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
  
        <div slot="footer" class="dialog-footer">
          <el-button @click="taskDetailDialogVisible = false">关 闭</el-button>
          <el-button
            v-if="selectedTask && !selectedTask.completed"
            type="primary"
            @click="completeTaskWithNotes"
          >
            完成任务
          </el-button>
        </div>
      </el-dialog>
    </div>
  </template>
  
  <script>
  export default {
    name: "PersonalManager",
    data() {
      return {
        // 当前个案管理师信息
        currentManager: {
          id: "M001",
          name: "刘管理",
          avatar:
            "https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png",
          department: "随访中心",
          title: "高级个案管理师",
        },
  
        // 搜索查询
        searchQuery: "",
        patientListTab: "all",
  
        // 选中的患者
        selectedPatient: null,
  
        // 回复内容
        replyContent: "",
        uploadFiles: [],
        forwardTarget: null, // 转发目标
  
        // 对话框
        templateDialogVisible: false,
        editTemplateDialogVisible: false,
        abnormalDialogVisible: false,
        taskDetailDialogVisible: false,
  
        // 模板相关
        currentTemplateAction: "add",
        currentTemplateIndex: -1,
        currentTemplate: {
          title: "",
          content: "",
        },
  
        // 选中的任务
        selectedTask: null,
  
        // 快捷回复模板
        replyTemplates: [
          {
            title: "随访提醒",
            content:
              "您好，根据随访计划，您的下一次随访时间为下周三上午，请准时参加。如有时间冲突，请及时与我联系调整。",
          },
          {
            title: "问卷填写提醒",
            content:
              "您好，请记得填写我们上次发送给您的生活质量评估问卷，这对于您的随访评估非常重要。如有任何疑问，请随时联系。",
          },
          {
            title: "随访反馈回复",
            content:
              "感谢您的反馈，我们已记录您的情况。如有任何不适或问题，请立即告知我们，我们会协助您安排相关处理。",
          },
          {
            title: "用药提醒",
            content:
              "请务必按照医生的建议按时服药，保持良好的用药习惯对疾病控制非常重要。如有任何副作用，请及时告知我们。",
          },
          {
            title: "检查结果通知",
            content:
              "您的最新检查结果已出，医生已进行评估，结果显示情况稳定。详细报告已上传至您的电子病历，您可以在APP上查看。",
          },
        ],
  
        // 转发选项
        forwardOptions: [
          { label: "张医生（心内科）", value: "doctor1" },
          { label: "王医生（消化科）", value: "doctor2" },
          { label: "李医生（肿瘤科）", value: "doctor3" },
          { label: "赵主任（项目管理）", value: "manager1" },
          { label: "陈主任（随访中心）", value: "manager2" },
        ],
  
        // 患者列表数据
        patients: [
          {
            id: "P001",
            name: "许昊然",
            age: 65,
            gender: "male",
            recordNum: "HF20245001",
            phone: "13812345678",
            planStatus: "进行中",
            status: "正常",
            phase: "术后随访",
            diagnosis: "冠心病，高血压",
            satisfaction: 4,
            compliance: 85,
            nextFollowUp: "2024-06-10",
            avatar:
              "https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png",
            unreadCount: 2,
            abnormalAlerts: [
              {
                title: "血压波动异常",
                level: "warning",
                description: "患者近3天收缩压大于140mmHg，波动明显",
                solution: "",
              },
            ],
            recentTasks: [
              {
                id: "T001",
                title: "电话随访",
                time: "2024-05-25 09:30",
                description: "了解患者血压控制情况及药物服用情况，记录不良反应",
                completed: true,
                notes: "患者血压稳定在130/85mmHg，无明显不良反应，按时服药。",
              },
              {
                id: "T002",
                title: "生活方式指导",
                time: "2024-06-02 14:00",
                description: "指导患者调整饮食结构，减少盐分摄入，增加适当运动",
                completed: false,
              },
              {
                id: "T003",
                title: "检查报告解读",
                time: "2024-06-10 10:30",
                description: "解读最新心电图和血脂检查结果，制定后续随访方案",
                completed: false,
                subtasks: [
                  { title: "获取检查报告", completed: false },
                  { title: "联系医生解读", completed: false },
                  { title: "向患者说明", completed: false },
                  { title: "记录患者反馈", completed: false },
                ],
              },
            ],
            messages: [
              {
                fromPatient: true,
                sender: "许昊然",
                role: "患者",
                content:
                  "管理师你好，我的血压最近有点波动，早上有时候会感到头晕。",
                timestamp: new Date(2024, 4, 15, 9, 23),
                read: true,
              },
              {
                fromPatient: false,
                sender: "刘管理",
                role: "个案管理师",
                content:
                  "张先生您好，请您测量一下最近几天的血压并记录下来。您头晕的症状持续多久？有没有其他不适？",
                timestamp: new Date(2024, 4, 15, 10, 5),
                read: true,
                status: "replied",
              },
              {
                fromPatient: true,
                sender: "许昊然",
                role: "患者",
                content:
                  "我记录了三天的血压，早上大概是150/95，头晕一般持续十几分钟就好了。",
                timestamp: new Date(2024, 4, 15, 10, 18),
                read: true,
                images: [
                  "https://shadow.elemecdn.com/app/element/hamburger.9cf7b091-55e9-11e9-a976-7f4d0b07eef6.png",
                ],
              },
              {
                fromPatient: false,
                sender: "刘管理",
                role: "个案管理师",
                content:
                  "您的血压偏高，我已将您的情况转交给王医生，他会对您的用药方案进行调整。同时建议您：<br>1. 减少盐的摄入量<br>2. 每天测量并记录血压，最好早晚各一次<br>3. 适当增加有氧运动",
                timestamp: new Date(2024, 4, 15, 10, 30),
                read: true,
                status: "forwarded",
                forwardInfo: {
                  title: "已转交王医生处理",
                  type: "info",
                  desc: "已将患者血压异常情况转交心内科王医生评估",
                },
              },
              {
                fromPatient: false,
                sender: "王医生",
                role: "主治医师",
                content:
                  "张先生，您好。根据您的血压情况，建议将降压药剂量调整为每天早晚各一次，每次一片。一周后请再次测量血压并反馈。若头晕症状加重，请立即就医。",
                timestamp: new Date(2024, 4, 15, 14, 25),
                read: true,
                processInfo: "由心内科王医生回复",
              },
              {
                fromPatient: true,
                sender: "许昊然",
                role: "患者",
                content:
                  "好的，我会按照医生的建议调整用药。另外，我这两天有点咳嗽，需要吃点什么药吗？",
                timestamp: new Date(2024, 5, 18, 8, 45),
                read: false,
              },
              {
                fromPatient: true,
                sender: "许昊然",
                role: "患者",
                content: "这是我最近的血压记录表",
                timestamp: new Date(2024, 5, 18, 8, 46),
                read: false,
                attachments: [
                  {
                    name: "血压记录表.xlsx",
                    url: "#",
                    size: "15KB",
                  },
                ],
              },
            ],
          },
          {
            id: "P002",
            name: "苏雅婷",
            age: 58,
            gender: "male",
            recordNum: "HF20245002",
            phone: "13987654321",
            planStatus: "已延期",
            status: "异常",
            phase: "维持期",
            diagnosis: "2型糖尿病",
            satisfaction: 3,
            compliance: 65,
            nextFollowUp: "2024-06-15",
            avatar:
              "https://cube.elemecdn.com/9/c2/f0ee8a3c7c9638a54940382568c9cpng.png",
            unreadCount: 1,
            abnormalAlerts: [
              {
                title: "血糖控制不佳",
                level: "danger",
                description: "近期血糖监测值连续3次超过11mmol/L",
                solution: "",
              },
              {
                title: "随访依从性差",
                level: "warning",
                description: "患者两次未按时完成随访问卷填写",
                solution: "",
              },
            ],
            recentTasks: [
              {
                id: "T004",
                title: "血糖监测记录核查",
                time: "2024-05-20 11:00",
                description: "检查患者血糖监测记录是否完整，分析波动情况",
                completed: true,
                notes: "患者监测记录不完整，发现连续3天血糖值偏高。",
              },
              {
                id: "T005",
                title: "饮食指导随访",
                time: "2024-05-27 15:30",
                description: "针对患者血糖控制情况提供饮食调整建议",
                completed: false,
              },
            ],
            messages: [
              {
                fromPatient: true,
                sender: "苏雅婷",
                role: "患者",
                content: "我最近血糖总是高，该怎么办？这个月我吃的也不多啊。",
                timestamp: new Date(2024, 5, 17, 10, 20),
                read: false,
              },
              {
                fromPatient: false,
                sender: "刘管理",
                role: "个案管理师",
                content:
                  "李先生您好，请您把最近一周的血糖记录发给我。请问您按时服用降糖药物了吗？另外，您最近的饮食情况如何，有没有特别的变化？",
                timestamp: new Date(2024, 5, 17, 11, 5),
                read: true,
                status: "replied",
              },
            ],
          },
          {
            id: "P003",
            name: "林书辰",
            age: 72,
            gender: "male",
            recordNum: "HF20245003",
            phone: "13712345678",
            planStatus: "按计划",
            status: "异常",
            phase: "术后恢复期",
            diagnosis: "肺癌术后",
            satisfaction: 5,
            compliance: 90,
            nextFollowUp: "2024-06-05",
            avatar:
              "https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png",
            unreadCount: 3,
            abnormalAlerts: [
              {
                title: "伤口愈合异常",
                level: "danger",
                description: "患者报告伤口有红肿现象，需要及时处理",
                solution: "",
              },
            ],
            recentTasks: [
              {
                id: "T006",
                title: "术后恢复随访",
                time: "2024-05-28 10:00",
                description: "评估患者术后恢复情况，关注伤口愈合状况",
                completed: false,
              },
            ],
            messages: [
              {
                fromPatient: true,
                sender: "林书辰",
                role: "患者",
                content: "管理师，我伤口好像有些红肿，这个正常吗？",
                timestamp: new Date(2024, 5, 17, 9, 10),
                read: false,
              },
              {
                fromPatient: true,
                sender: "林书辰",
                role: "患者",
                content: "这是我伤口的照片",
                timestamp: new Date(2024, 5, 17, 9, 11),
                read: false,
                images: [
                  "https://shadow.elemecdn.com/a/17/d43b41abc8c413096227590fb94aejpeg.jpeg",
                ],
              },
              {
                fromPatient: true,
                sender: "林书辰",
                role: "患者",
                content: "我最近咳嗽有些加重，需要去医院吗？",
                timestamp: new Date(2024, 5, 17, 14, 25),
                read: false,
              },
            ],
          },
          {
            id: "P004",
            name: "程若曦",
            age: 45,
            gender: "female",
            recordNum: "HF20245004",
            phone: "13612345678",
            planStatus: "按计划",
            status: "正常",
            phase: "治疗期",
            diagnosis: "甲状腺功能亢进",
            satisfaction: 4.5,
            compliance: 95,
            nextFollowUp: "2024-06-20",
            avatar:
              "https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png",
            unreadCount: 0,
            abnormalAlerts: [],
            recentTasks: [
              {
                id: "T007",
                title: "心率监测随访",
                time: "2024-05-25 14:30",
                description: "收集患者最近心率记录，评估治疗效果",
                completed: true,
                notes: "患者心率控制良好，在80-90次/分钟范围内。",
              },
              {
                id: "T008",
                title: "生活质量评估",
                time: "2024-06-10 16:00",
                description: "发送生活质量评估表，收集患者最近生活状况反馈",
                completed: false,
              },
            ],
            messages: [
              {
                fromPatient: true,
                sender: "程若曦",
                role: "患者",
                content: "刘管理师，我按照您的建议调整了饮食，感觉好多了！",
                timestamp: new Date(2024, 5, 5, 10, 30),
                read: true,
              },
              {
                fromPatient: false,
                sender: "刘管理",
                role: "个案管理师",
                content:
                  "赵女士，很高兴听到您的症状有所改善！请您继续记录心率情况，有没有感到心慌、出汗等症状？",
                timestamp: new Date(2024, 5, 5, 11, 15),
                read: true,
                status: "replied",
              },
              {
                fromPatient: true,
                sender: "程若曦",
                role: "患者",
                content:
                  "心率现在基本在80-90之间，偶尔会有点心慌，但比之前好多了。出汗的情况也少了。",
                timestamp: new Date(2024, 5, 5, 11, 30),
                read: true,
              },
              {
                fromPatient: false,
                sender: "刘管理",
                role: "个案管理师",
                content:
                  "您的恢复情况很好，我已经将您的情况反馈给张医生，他对您目前的治疗效果很满意。请继续保持良好的作息习惯，定期监测心率。下次复诊时间是6月20日，到时我会提前提醒您。",
                timestamp: new Date(2024, 5, 5, 14, 20),
                read: true,
                status: "forwarded",
                forwardInfo: {
                  title: "已转交张医生评估",
                  type: "success",
                  desc: "医生已确认治疗效果良好，无需调整治疗方案",
                },
              },
            ],
          },
          {
            id: "P005",
            name: "郭子轩",
            age: 68,
            gender: "male",
            recordNum: "HF20245005",
            phone: "13512345678",
            planStatus: "已完成",
            status: "正常",
            phase: "随访结束",
            diagnosis: "前列腺增生",
            satisfaction: 5,
            compliance: 100,
            nextFollowUp: "已结束",
            avatar:
              "https://cube.elemecdn.com/9/c2/f0ee8a3c7c9638a54940382568c9cpng.png",
            unreadCount: 1,
            abnormalAlerts: [],
            recentTasks: [
              {
                id: "T009",
                title: "随访总结报告",
                time: "2024-05-15 09:00",
                description: "撰写随访结束总结报告，分析患者恢复情况",
                completed: true,
                notes: "患者恢复良好，症状完全缓解，随访计划已全部完成。",
              },
            ],
            messages: [
              {
                fromPatient: false,
                sender: "刘管理",
                role: "个案管理师",
                content:
                  "钱先生您好，您的随访计划已经全部完成，根据评估您的恢复情况良好。如有任何不适，请随时联系我们。",
                timestamp: new Date(2024, 4, 20, 14, 0),
                read: true,
                status: "replied",
              },
              {
                fromPatient: true,
                sender: "郭子轩",
                role: "患者",
                content:
                  "谢谢刘管理师，我现在感觉很好，不过我想问问以后需要定期复查吗？",
                timestamp: new Date(2024, 5, 18, 9, 30),
                read: false,
              },
            ],
          },
        ],
      };
    },
    computed: {
      filteredPatients() {
        let result = this.patients;
  
        // 标签筛选
        if (this.patientListTab === "pending") {
          result = result.filter((patient) => patient.unreadCount > 0);
        } else if (this.patientListTab === "abnormal") {
          result = result.filter((patient) => patient.status === "异常");
        }
  
        // 关键词搜索
        if (this.searchQuery) {
          const query = this.searchQuery.toLowerCase();
          result = result.filter(
            (patient) =>
              patient.name.toLowerCase().includes(query) ||
              patient.recordNum.toLowerCase().includes(query)
          );
        }
  
        return result;
      },
  
      // 判断是否可以发送回复
      canSendReply() {
        return (
          (this.replyContent || this.uploadFiles.length > 0) &&
          this.selectedPatient
        );
      },
    },
    methods: {
      // 患者列表相关方法
      filterPatients() {
        // 搜索过滤逻辑在computed中实现
      },
  
      handleTabClick() {
        // 切换标签逻辑在computed中实现
      },
  
      selectPatient(patient) {
        this.selectedPatient = patient;
        this.forwardTarget = null;
        this.replyContent = "";
        this.uploadFiles = [];
  
        // 标记该患者的所有消息为已读
        if (patient && patient.messages) {
          patient.messages.forEach((msg) => {
            if (msg.fromPatient) {
              msg.read = true;
            }
          });
          // 重置未读数
          patient.unreadCount = 0;
        }
      },
  
      getPendingCount() {
        return this.patients.filter((patient) => patient.unreadCount > 0).length;
      },
  
      getAbnormalCount() {
        return this.patients.filter((patient) => patient.status === "异常")
          .length;
      },
  
      getLatestMessage(patient) {
        if (patient.messages && patient.messages.length > 0) {
          const lastMsg = patient.messages[patient.messages.length - 1];
          // 去掉HTML标签，截取内容
          const content = lastMsg.content.replace(/<[^>]*>/g, "");
          return content.length > 20 ? content.substring(0, 20) + "..." : content;
        }
        return "无消息记录";
      },
  
      getLatestMessageTime(patient) {
        if (patient.messages && patient.messages.length > 0) {
          const lastMsg = patient.messages[patient.messages.length - 1];
          return this.formatTime(lastMsg.timestamp);
        }
        return "";
      },
  
      // 消息区域相关方法
      getMessageClass(message) {
        if (message.fromPatient) {
          return "patient-message";
        } else if (message.sender === this.currentManager.name) {
          return "manager-message";
        } else {
          return "doctor-message";
        }
      },
  
      getMessageAvatar(message) {
        if (message.fromPatient) {
          return this.selectedPatient.avatar;
        } else if (message.sender === this.currentManager.name) {
          return this.currentManager.avatar;
        } else {
          // 简单的默认头像
          return "https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png";
        }
      },
  
      showDateDivider(message, index) {
        if (index === 0) return true;
  
        const prevMsg = this.selectedPatient.messages[index - 1];
        const prevDate = new Date(prevMsg.timestamp);
        const currDate = new Date(message.timestamp);
  
        return prevDate.toDateString() !== currDate.toDateString();
      },
  
      formatFullDate(date) {
        if (!(date instanceof Date)) {
          date = new Date(date);
        }
  
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
  
        if (date.toDateString() === today.toDateString()) {
          return "今天";
        } else if (date.toDateString() === yesterday.toDateString()) {
          return "昨天";
        } else {
          return `${date.getFullYear()}年${
            date.getMonth() + 1
          }月${date.getDate()}日`;
        }
      },
  
      formatTime(date) {
        if (!(date instanceof Date)) {
          date = new Date(date);
        }
  
        const now = new Date();
        const diff = Math.floor((now - date) / 1000); // 秒差
  
        if (diff < 60) {
          return "刚刚";
        } else if (diff < 3600) {
          return Math.floor(diff / 60) + "分钟前";
        } else if (diff < 86400 && date.getDate() === now.getDate()) {
          return `${String(date.getHours()).padStart(2, "0")}:${String(
            date.getMinutes()
          ).padStart(2, "0")}`;
        } else if (diff < 172800 && date.getDate() === now.getDate() - 1) {
          return `昨天 ${String(date.getHours()).padStart(2, "0")}:${String(
            date.getMinutes()
          ).padStart(2, "0")}`;
        } else {
          return `${date.getMonth() + 1}月${date.getDate()}日`;
        }
      },
  
      getStatusText(status) {
        switch (status) {
          case "replied":
            return "已回复";
          case "forwarded":
            return "已转发";
          case "processing":
            return "处理中";
          default:
            return "";
        }
      },
  
      // 回复相关方法
      handleUploadImage() {
        this.$message.info("此处应打开图片上传对话框");
        // 模拟添加一个图片文件
        this.uploadFiles.push({
          name: "图片.jpg",
          type: "image",
          url: "data:image/jpeg;base64,/9j/4AAQ...",
        });
      },
  
      handleUploadFile() {
        this.$message.info("此处应打开文件上传对话框");
        // 模拟添加一个文件
        this.uploadFiles.push({
          name: "健康指导手册.pdf",
          type: "file",
          size: "245KB",
        });
      },
  
      removeUploadFile(index) {
        this.uploadFiles.splice(index, 1);
      },
  
      handleTemplateCommand(command) {
        if (command === "manage") {
          this.templateDialogVisible = true;
        } else {
          // 插入模板内容
          this.replyContent = this.replyTemplates[command].content;
        }
      },
  
      handleForwardCommand(command) {
        this.forwardTarget = command;
        this.$message.info(`留言将转发给 ${this.getForwardTargetName(command)}`);
      },
  
      getForwardTargetName(targetId) {
        const target = this.forwardOptions.find((opt) => opt.value === targetId);
        return target ? target.label : "未知接收人";
      },
  
      sendReply() {
        if (!this.selectedPatient) return;
  
        // 构建新消息对象
        const newMessage = {
          fromPatient: false,
          sender: this.currentManager.name,
          role: this.currentManager.title,
          content: this.replyContent || "已收到附件",
          timestamp: new Date(),
          read: true,
          status: this.forwardTarget ? "forwarded" : "replied",
        };
  
        // 如果是转发
        if (this.forwardTarget) {
          const targetName = this.getForwardTargetName(this.forwardTarget);
          newMessage.forwardInfo = {
            title: `已转发给 ${targetName}`,
            type: "info",
            desc: `已将患者留言转发给${targetName}处理`,
          };
        }
  
        // 如果有上传文件，添加到消息中
        if (this.uploadFiles.length > 0) {
          const attachments = this.uploadFiles
            .filter((file) => file.type === "file")
            .map((file) => ({
              name: file.name,
              url: file.url || "#",
              size: file.size || "0KB",
            }));
  
          if (attachments.length > 0) {
            newMessage.attachments = attachments;
          }
  
          const images = this.uploadFiles
            .filter((file) => file.type === "image")
            .map((file) => file.url);
          if (images.length > 0) {
            newMessage.images = images;
          }
        }
  
        // 添加到消息列表
        this.selectedPatient.messages.push(newMessage);
  
        // 如果是转发并且需要模拟医生回复
        if (this.forwardTarget && this.forwardTarget.startsWith("doctor")) {
          // 延迟1秒后模拟医生回复
          setTimeout(() => {
            const doctorName =
              this.forwardTarget === "doctor1"
                ? "张医生"
                : this.forwardTarget === "doctor2"
                ? "王医生"
                : "李医生";
  
            const doctorReply = {
              fromPatient: false,
              sender: doctorName,
              role: "主治医师",
              content: `感谢转发。我已查看患者情况，${this.selectedPatient.name}的问题我会尽快处理。请告知患者按现有方案继续治疗，有任何不适随时联系。`,
              timestamp: new Date(Date.now() + 60000), // 1分钟后
              read: true,
              processInfo: `由${doctorName}回复`,
            };
  
            this.selectedPatient.messages.push(doctorReply);
          }, 3000);
        }
  
        // 清空回复框
        this.replyContent = "";
        this.uploadFiles = [];
        this.forwardTarget = null;
  
        // 提示发送成功
        this.$message.success("回复已发送");
      },
  
      downloadAttachment(attachment) {
        this.$message.info(`正在下载: ${attachment.name}`);
        // 实际项目中这里应该是真实下载逻辑
      },
  
      // 患者操作相关方法
      handlePatientAction(command) {
        if (!this.selectedPatient) return;
  
        switch (command) {
          case "phone":
            this.$message.info(
              `正在拨打患者${this.selectedPatient.name}的电话: ${this.selectedPatient.phone}`
            );
            break;
          case "record":
            this.$message.info(`查看${this.selectedPatient.name}的随访记录`);
            break;
          case "plan":
            this.$message.info(`查看${this.selectedPatient.name}的随访计划`);
            break;
          case "report":
            this.$message.info(`查看${this.selectedPatient.name}的异常报告`);
            break;
        }
      },
  
      // 模板管理相关方法
      addNewTemplate() {
        this.currentTemplateAction = "add";
        this.currentTemplateIndex = -1;
        this.currentTemplate = {
          title: "",
          content: "",
        };
        this.editTemplateDialogVisible = true;
      },
  
      editTemplate(template) {
        this.currentTemplateAction = "edit";
        this.currentTemplateIndex = this.replyTemplates.findIndex(
          (t) => t.title === template.title
        );
        this.currentTemplate = { ...template };
        this.editTemplateDialogVisible = true;
      },
  
      deleteTemplate(index) {
        this.$confirm("确定要删除此模板吗?", "提示", {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        })
          .then(() => {
            this.replyTemplates.splice(index, 1);
            this.$message.success("删除成功");
          })
          .catch(() => {});
      },
  
      saveTemplate() {
        if (!this.currentTemplate.title || !this.currentTemplate.content) {
          this.$message.warning("模板名称和内容不能为空");
          return;
        }
  
        if (this.currentTemplateAction === "add") {
          this.replyTemplates.push({ ...this.currentTemplate });
          this.$message.success("新增模板成功");
        } else {
          this.replyTemplates[this.currentTemplateIndex] = {
            ...this.currentTemplate,
          };
          this.$message.success("更新模板成功");
        }
  
        this.editTemplateDialogVisible = false;
      },
  
      // 状态和颜色相关方法
      getStatusType(status) {
        switch (status) {
          case "按计划":
            return "success";
          case "进行中":
            return "primary";
          case "已延期":
            return "warning";
          case "已完成":
            return "info";
          default:
            return "";
        }
      },
  
      getPhaseType(phase) {
        switch (phase) {
          case "治疗期":
            return "danger";
          case "术后恢复期":
            return "warning";
          case "术后随访":
            return "primary";
          case "维持期":
            return "success";
          case "随访结束":
            return "info";
          default:
            return "";
        }
      },
  
      getComplianceColor(value) {
        if (value >= 90) return "#67C23A"; // 优秀
        if (value >= 80) return "#409EFF"; // 良好
        if (value >= 70) return "#E6A23C"; // 一般
        return "#F56C6C"; // 需关注
      },
  
      // 快捷操作方法
      viewTasks() {
        this.$message.info(`查看${this.selectedPatient.name}的随访任务列表`);
      },
  
      editPlan() {
        this.$message.info(`编辑${this.selectedPatient.name}的随访计划`);
      },
  
      handleAbnormal() {
        if (
          !this.selectedPatient.abnormalAlerts ||
          this.selectedPatient.abnormalAlerts.length === 0
        ) {
          this.$message.info("该患者当前无异常情况需要处理");
          return;
        }
        this.abnormalDialogVisible = true;
      },
  
      resolveAbnormal(alert, index) {
        if (!alert.solution) {
          this.$message.warning("请先填写处理方案");
          return;
        }
  
        this.$confirm(`确认已解决"${alert.title}"问题?`, "确认", {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "success",
        })
          .then(() => {
            // 实际项目中应该调用API记录处理结果
            this.selectedPatient.abnormalAlerts.splice(index, 1);
  
            // 如果没有异常了，更新患者状态
            if (this.selectedPatient.abnormalAlerts.length === 0) {
              this.selectedPatient.status = "正常";
            }
  
            this.$message.success(`已成功解决${alert.title}问题`);
  
            // 如果没有异常了，关闭对话框
            if (this.selectedPatient.abnormalAlerts.length === 0) {
              this.abnormalDialogVisible = false;
            }
          })
          .catch(() => {});
      },
  
      escalateAbnormal(alert, index) {
        if (!alert.solution) {
          this.$message.warning("请先填写处理方案后再上报");
          return;
        }
  
        this.$confirm(`确认将"${alert.title}"问题上报给医生?`, "确认", {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        })
          .then(() => {
            // 实际项目中应该调用API进行上报
            this.$message.success(`已将${alert.title}问题上报给医生，等待处理`);
  
            // 模拟更新状态
            this.selectedPatient.abnormalAlerts[index].level = "info";
            this.selectedPatient.abnormalAlerts[index].description +=
              "（已上报医生处理）";
          })
          .catch(() => {});
      },
  
      viewSatisfaction() {
        this.$message.info(`查看${this.selectedPatient.name}的满意度报告`);
      },
  
      // 任务相关方法
      markTaskDone(task) {
        this.$confirm(`确认将任务"${task.title}"标记为已完成?`, "确认", {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "success",
        })
          .then(() => {
            task.completed = true;
            task.notes = "已完成，无特殊情况";
            this.$message.success("任务已标记为完成");
          })
          .catch(() => {});
      },
  
      viewTaskDetail(task) {
        this.selectedTask = task;
        this.taskDetailDialogVisible = true;
      },
  
      markSubtaskDone(subtask, index) {
        subtask.completed = true;
        // 检查是否所有子任务都完成了
        if (this.selectedTask && this.selectedTask.subtasks) {
          const allCompleted = this.selectedTask.subtasks.every(
            (task) => task.completed
          );
          if (allCompleted) {
            this.$message.info("所有子任务已完成，可以标记主任务为完成状态");
          }
        }
      },
  
      completeTaskWithNotes() {
        this.$prompt("请输入任务完成记录", "完成任务", {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          inputType: "textarea",
          inputPlaceholder: "请简要描述任务执行情况和结果...",
        })
          .then(({ value }) => {
            this.selectedTask.completed = true;
            this.selectedTask.notes = value || "已完成";
            this.taskDetailDialogVisible = false;
            this.$message.success("任务已标记为完成");
          })
          .catch(() => {
            // 用户取消操作时不做任何处理
          });
      },
      // 添加到 methods 中
      scrollToBottom() {
        this.$nextTick(() => {
          const container = document.querySelector(".message-content");
          if (container) {
            container.scrollTop = container.scrollHeight;
          }
        });
      },
    },
    mounted() {
      // 初始化时可以默认选中第一个患者
      if (this.patients && this.patients.length > 0) {
        this.selectPatient(this.patients[0]);
      }
  
      // 从本地存储加载模板数据
      const savedTemplates = localStorage.getItem("replyTemplates");
      if (savedTemplates) {
        try {
          this.replyTemplates = JSON.parse(savedTemplates);
        } catch (e) {
          console.error("加载回复模板失败", e);
        }
      }
    },
  };
  </script>
  
  <style scoped>
  .message-container {
    display: flex;
    height: calc(100vh - 120px);
    background-color: #f5f7fa;
  }
  
  /* 左侧患者列表样式 */
  .patient-list {
    width: 280px;
    border-right: 1px solid #ebeef5;
    display: flex;
    flex-direction: column;
    background-color: #fff;
  }
  
  .search-container {
    padding: 15px;
  }
  
  .patient-list-content {
    flex: 1;
    overflow-y: auto;
  }
  
  .patient-item {
    padding: 12px 15px;
    border-bottom: 1px solid #ebeef5;
    cursor: pointer;
    display: flex;
    align-items: center;
  }
  
  .patient-item:hover {
    background-color: #f5f7fa;
  }
  
  .patient-item.active {
    background-color: #ecf5ff;
  }
  
  .patient-item.abnormal-patient {
    background-color: #fff8f8;
  }
  
  .avatar-container {
    position: relative;
    margin-right: 12px;
  }
  
  .item-unread {
    position: absolute;
    top: -5px;
    right: -5px;
  }
  
  .item-abnormal {
    position: absolute;
    bottom: -5px;
    right: -5px;
  }
  
  .patient-info {
    flex: 1;
    overflow: hidden;
  }
  
  .patient-name {
    font-size: 14px;
    font-weight: bold;
    margin-bottom: 5px;
    display: flex;
    align-items: center;
  }
  
  .patient-age {
    font-size: 12px;
    color: #909399;
    margin-left: 5px;
    font-weight: normal;
  }
  
  .latest-message {
    font-size: 12px;
    color: #606266;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .message-time {
    font-size: 12px;
    color: #909399;
    white-space: nowrap;
  }
  
  .pending-badge,
  .abnormal-badge {
    margin-top: -5px;
  }
  
  /* 中间消息区域样式 */
  .message-area {
    flex: 1;
    display: flex;
    flex-direction: column;
    background-color: #f5f7fa;
  }
  
  .message-header {
    padding: 15px;
    background-color: #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .patient-title {
    display: flex;
    align-items: center;
  }
  
  .patient-title .name {
    font-size: 16px;
    font-weight: bold;
    margin-right: 10px;
  }
  
  .patient-title .record-number {
    font-size: 14px;
    color: #606266;
    margin-right: 10px;
  }
  
  .message-content {
    flex: 1;
    overflow-y: auto;
    padding: 15px;
  }
  
  .message-block {
    margin-bottom: 20px;
  }
  
  .message-date-divider {
    text-align: center;
    margin: 10px 0;
    color: #909399;
    font-size: 12px;
  }
  
  .message-date-divider span {
    background-color: #f5f7fa;
    padding: 0 10px;
    position: relative;
  }
  
  .message-date-divider::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    height: 1px;
    background-color: #ebeef5;
    top: 50%;
  }
  
  .message-wrapper {
    display: flex;
    margin-bottom: 10px;
  }
  
  .patient-message {
    flex-direction: row;
  }
  
  .manager-message,
  .doctor-message {
    flex-direction: row-reverse;
  }
  
  .avatar {
    margin: 0 10px;
  }
  
  .message-bubble {
    max-width: 70%;
    border-radius: 8px;
    padding: 10px;
    position: relative;
  }
  
  .patient-message .message-bubble {
    background-color: #fff;
    border: 1px solid #ebeef5;
  }
  
  .manager-message .message-bubble {
    background-color: #ecf5ff;
    border: 1px solid #d9ecff;
  }
  
  .doctor-message .message-bubble {
    background-color: #f0f9eb;
    border: 1px solid #e1f3d8;
  }
  
  .message-info {
    margin-bottom: 5px;
    font-size: 12px;
  }
  
  .sender-name {
    font-weight: bold;
    margin-right: 5px;
  }
  
  .sender-role {
    color: #909399;
    margin-right: 10px;
  }
  
  .message-time {
    color: #c0c4cc;
  }
  
  .message-status {
    font-size: 12px;
    padding: 2px 6px;
    border-radius: 10px;
    margin-left: 5px;
  }
  
  .status-replied {
    background-color: #ecf5ff;
    color: #409eff;
  }
  
  .status-forwarded {
    background-color: #f0f9eb;
    color: #67c23a;
  }
  
  .status-processing {
    background-color: #fdf6ec;
    color: #e6a23c;
  }
  
  .message-text {
    font-size: 14px;
    line-height: 1.5;
    word-break: break-word;
  }
  
  .message-images {
    display: flex;
    flex-wrap: wrap;
    margin-top: 8px;
  }
  
  .message-image {
    width: 100px;
    height: 100px;
    margin-right: 8px;
    margin-bottom: 8px;
    object-fit: cover;
    border-radius: 4px;
  }
  
  .message-attachments {
    margin-top: 8px;
  }
  
  .message-attachment {
    display: flex;
    align-items: center;
    background-color: #f4f6f8;
    border-radius: 4px;
    padding: 5px 10px;
    margin-bottom: 5px;
  }
  
  .message-attachment i {
    margin-right: 5px;
  }
  
  .process-info {
    margin-top: 8px;
    font-size: 12px;
    color: #909399;
  }
  
  .process-detail {
    display: flex;
    align-items: center;
  }
  
  .process-detail i {
    margin-right: 5px;
  }
  
  .forward-info {
    margin-top: 8px;
  }
  
  .message-reply {
    background-color: #fff;
    border-top: 1px solid #ebeef5;
    padding: 15px;
  }
  
  .reply-toolbar {
    margin-bottom: 10px;
    display: flex;
    flex-wrap: wrap;
  }
  
  .reply-toolbar .el-button {
    margin-right: 10px;
    margin-bottom: 5px;
  }
  
  .upload-preview {
    margin-top: 10px;
  }
  
  .upload-file {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    padding: 5px 10px;
    margin-bottom: 5px;
  }
  
  .forward-target-info {
    margin: 10px 0;
  }
  
  .reply-actions {
    margin-top: 15px;
    text-align: right;
  }
  
  .no-patient-selected {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: #909399;
  }
  
  .no-patient-selected i {
    font-size: 60px;
    margin-bottom: 15px;
  }
  
  /* 右侧患者详情样式 */
  .patient-detail {
    width: 300px;
    background-color: #fff;
    border-left: 1px solid #ebeef5;
    overflow-y: auto;
  }
  
  .patient-profile {
    padding: 20px;
  }
  
  .profile-header {
    text-align: center;
    margin-bottom: 20px;
  }
  
  .profile-header h3 {
    margin: 10px 0 5px;
    font-size: 18px;
    color: #303133;
  }
  
  .basic-info {
    color: #606266;
    font-size: 14px;
  }
  
  .basic-info p {
    margin: 5px 0;
  }
  
  .quick-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 20px;
  }
  
  .abnormal-alerts {
    margin-bottom: 20px;
  }
  
  .alert-item {
    margin-bottom: 10px;
  }
  
  .divider-title.warning {
    color: #e6a23c;
  }
  
  .recent-tasks {
    margin-top: 10px;
  }
  
  .task-item {
    border-bottom: 1px solid #ebeef5;
    padding: 10px 0;
  }
  
  .task-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 5px;
  }
  
  .task-title {
    font-weight: bold;
    display: flex;
    align-items: center;
  }
  
  .task-icon {
    margin-right: 5px;
  }
  
  .task-time {
    font-size: 12px;
    color: #909399;
    margin-bottom: 5px;
  }
  
  .task-desc {
    font-size: 13px;
    color: #606266;
    margin-bottom: 8px;
  }
  
  .task-footer {
    display: flex;
    justify-content: flex-end;
  }
  
  .abnormal-form {
    margin-top: 10px;
  }
  
  .abnormal-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;
  }
  
  /* 响应式适配 */
  @media screen and (max-width: 1200px) {
    .patient-detail {
      width: 250px;
    }
  }
  
  @media screen and (max-width: 768px) {
    .message-container {
      flex-direction: column;
      height: auto;
    }
  
    .patient-list {
      width: 100%;
      height: 300px;
    }
  
    .message-area {
      height: 50vh;
    }
  
    .patient-detail {
      width: 100%;
      border-left: none;
      border-top: 1px solid #ebeef5;
    }
  }
  </style>
  